import React, { useState } from 'react';
import { CustomerAddressDto } from '../../../types/locations';
import { MapPin, Edit2, Trash2, CheckCircle, Star, Plus } from 'lucide-react';
import { CustomerAddressForm } from './CustomerAddressForm';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CustomerAddressesListProps {
  addresses: CustomerAddressDto[];
  onChange: (addresses: CustomerAddressDto[]) => void;
}

export const CustomerAddressesList: React.FC<CustomerAddressesListProps> = ({ 
  addresses, 
  onChange 
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddressDto | null>(null);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const handleDelete = () => {
    if (itemToDelete) {
      onChange(addresses.filter(a => a.id !== itemToDelete));
      setItemToDelete(null);
    }
  };

  const handleSetDefault = (addressId: string) => {
    const updated = addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId
    }));
    onChange(updated);
  };

  const openNewAddressForm = () => {
    setEditingAddress(null);
    setIsFormOpen(true);
  };

  const openEditAddressForm = (address: CustomerAddressDto) => {
    setEditingAddress(address);
    setIsFormOpen(true);
  };

  const handleSaveAddress = (data: any) => {
    if (data.id) {
      // Update existing
      onChange(addresses.map(a => a.id === data.id ? data : a));
    } else {
      // New address
      const newAddress = {
        ...data,
        id: `temp-${Date.now()}`, // Temporary ID
      };
      
      // If it's the first address or set as default, handle exclusivity
      let finalAddresses = [...addresses];
      if (data.isDefault) {
        finalAddresses = finalAddresses.map(a => ({ ...a, isDefault: false }));
      } else if (addresses.length === 0) {
        newAddress.isDefault = true;
      }
      
      onChange([...finalAddresses, newAddress]);
    }
    setIsFormOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 uppercase tracking-wider">
          <MapPin className="h-4 w-4 text-slate-500" />
          Direcciones de Entrega
        </h3>
        <Button
          type="button"
          size="sm"
          onClick={openNewAddressForm}
          variant="outline"
          className="h-8"
        >
          <Plus className="h-4 w-4 mr-1" />
          Nueva Dirección
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm font-medium">No hay direcciones registradas</p>
          <button
            type='button'
            onClick={openNewAddressForm}
            className="text-blue-600 hover:text-blue-800 text-xs mt-1 font-semibold"
          >
            Añadir la primera dirección
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((addr) => (
            <div
              key={addr.id}
              className={`border rounded-xl p-4 relative transition-all ${
                addr.isDefault 
                  ? 'border-blue-200 bg-blue-50/30' 
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="absolute top-4 right-4 flex gap-1">
                {addr.isDefault && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 uppercase">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Principal
                  </span>
                )}
              </div>

              <div className="pr-16">
                {addr.name && (
                  <h4 className="font-bold text-slate-900 text-sm mb-1">{addr.name}</h4>
                )}
                <p className="text-slate-600 text-sm line-clamp-1">{addr.addressLine}</p>
                <div className="text-xs text-slate-500 mt-1">
                  <p>{addr.postalCode} {addr.cityName}</p>
                  <p>{addr.provinceName}, {addr.countryName}</p>
                </div>
              </div>

              <div className="mt-4 flex justify-between items-center pt-3 border-t border-slate-100">
                {!addr.isDefault ? (
                  <button
                    type="button"
                    onClick={() => handleSetDefault(addr.id)}
                    className="text-[10px] text-slate-400 hover:text-blue-600 flex items-center gap-1 font-bold uppercase tracking-tight"
                  >
                    <Star className="h-3 w-3" />
                    Hacer principal
                  </button>
                ) : (
                  <div className="w-4" />
                )}
                
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditAddressForm(addr)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setItemToDelete(addr.id)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar dirección?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la dirección del listado. Los cambios se guardarán permanentemente cuando actualice el cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Address Form */}
      <CustomerAddressForm
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        address={editingAddress}
        onSave={handleSaveAddress}
      />
    </div>
  );
};
